import React from "react";
import LoadingSpinner from "./LoadingSpinner";

interface ButtonProps {
  variant: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  loadingText?: string;
}

const Button: React.FC<ButtonProps> = ({
  variant,
  size = "md",
  loading = false,
  disabled = false,
  children,
  onClick,
  type = "button",
  className = "",
  loadingText,
}) => {
  // Size mappings - enhanced for Culture Lab aesthetic
  const sizeClasses = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
  };

  // Base classes - enhanced with glassmorphism and Culture Lab styling
  const baseClasses = `
    font-semibold rounded-2xl transition-all duration-300 
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent
    inline-flex items-center justify-center
    backdrop-blur-sm border
    relative overflow-hidden
    group
  `
    .trim()
    .replace(/\s+/g, " ");

  // Enhanced variant mappings to match Culture Lab theme
  const variantClasses = {
    primary: `
      bg-gradient-to-r from-blue-600 to-purple-600
      hover:from-blue-700 hover:to-purple-700
      text-white border-transparent
      transform hover:scale-105
      shadow-lg hover:shadow-xl hover:shadow-blue-500/25
      focus:ring-blue-500/50
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:to-transparent before:opacity-0 before:transition-opacity before:duration-300
      hover:before:opacity-100
      disabled:bg-gradient-to-r disabled:from-gray-600 disabled:to-gray-700 
      disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none 
      disabled:shadow-none disabled:hover:before:opacity-0
    `,
    secondary: `
      bg-white/10 backdrop-blur-sm border-white/20
      text-white hover:bg-white/20 hover:border-white/30
      shadow-lg hover:shadow-xl
      focus:ring-white/50
      disabled:bg-white/5 disabled:border-white/10 disabled:text-white/50 
      disabled:cursor-not-allowed disabled:hover:bg-white/5
    `,
    outline: `
      bg-white/5 backdrop-blur-sm border-blue-400/50
      text-blue-400 hover:bg-blue-600/20 hover:border-blue-400/80 hover:text-blue-300
      shadow-lg hover:shadow-blue-500/20
      focus:ring-blue-400/50
      disabled:border-gray-400/30 disabled:text-gray-500 disabled:cursor-not-allowed 
      disabled:hover:bg-white/5 disabled:hover:text-gray-500 disabled:hover:border-gray-400/30
    `,
    ghost: `
      bg-transparent border-transparent
      text-gray-400 hover:text-white hover:bg-white/10
      backdrop-blur-sm
      focus:ring-white/30
      disabled:text-gray-600 disabled:cursor-not-allowed 
      disabled:hover:text-gray-600 disabled:hover:bg-transparent
    `,
  };

  // Loading spinner color mapping
  const getSpinnerColor = () => {
    switch (variant) {
      case "primary":
        return "white";
      case "secondary":
        return "white";
      case "outline":
        return "blue";
      case "ghost":
        return "gray";
      default:
        return "blue";
    }
  };

  // Combine all classes
  const buttonClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={isDisabled}
    >
      {loading ? (
        <>
          <LoadingSpinner size="sm" color={getSpinnerColor()} />
          <span className="ml-2">{loadingText || children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
